import behaviourPointsConstants from "@/constants/behaviourPointsConstants.js"

/**
 * @typedef {"award" | "redeem" | "correction"} BehaviourPointsOperation
 */

/**
 * LazyLoadSelect stores students as `{ value: { id, type }, label }` or `{ id, type }`.
 * @param {unknown} value
 * @returns {number | null}
 */
export function normalizeBehaviourStudentId(value) {
  if (value == null || value === "") return null

  if (typeof value === "object") {
    const nestedId = value?.value?.id ?? value?.id
    const parsed = Number(nestedId)
    return Number.isFinite(parsed) ? parsed : null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * @param {unknown} values
 * @returns {number[]}
 */
export function normalizeBehaviourStudentIds(values) {
  if (!Array.isArray(values)) {
    const singleId = normalizeBehaviourStudentId(values)
    return singleId != null ? [singleId] : []
  }

  return values
    .map((value) => normalizeBehaviourStudentId(value))
    .filter((id) => id != null)
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function extractFirstMessage(value) {
  if (Array.isArray(value)) {
    return String(value[0] ?? "").trim()
  }
  return String(value ?? "").trim()
}

/**
 * @param {string} apiKey
 * @param {BehaviourPointsOperation} operation
 * @returns {string | null}
 */
export function mapBehaviourPointsApiFieldToFormField(apiKey, operation) {
  const key = String(apiKey ?? "")
    .trim()
    .toLowerCase()
  if (key === "student_ids" || key.startsWith("student_ids.")) {
    return "studentIds"
  }
  if (key === "csv_file" || key === "file") {
    return "csvFile"
  }
  if (key === "positive_behavior_id") {
    return "positiveBehaviourId"
  }
  if (key === "notes") {
    return "awardDescription"
  }
  if (key === "points") {
    return operation === "redeem" ? "pointsUsed" : "points"
  }
  if (key === "reason") {
    return operation === "redeem" ? "rewardReason" : "reason"
  }
  return null
}

/**
 * @param {Record<string, unknown>} form
 * @returns {boolean}
 */
export function isBehaviourStudentCsvSelection(form) {
  return (
    form?.selectionMethod ===
      behaviourPointsConstants.STUDENT_SELECTION_METHOD.CSV &&
    Boolean(form?.csvFile)
  )
}

/**
 * @param {Record<string, unknown>} form
 * @returns {{
 *   positive_behavior_id: number
 *   student_ids: number[]
 *   notes?: string
 * } | FormData}
 */
export function buildAwardPointsPayload(form) {
  const positiveBehaviorId = Number(form?.positiveBehaviourId)
  const notes = String(form?.awardDescription ?? "").trim()

  if (isBehaviourStudentCsvSelection(form)) {
    const formData = new FormData()
    formData.append("positive_behavior_id", String(positiveBehaviorId))
    formData.append("csv_file", form.csvFile)
    if (notes) {
      formData.append("notes", notes)
    }
    return formData
  }

  const payload = {
    positive_behavior_id: positiveBehaviorId,
    student_ids: normalizeBehaviourStudentIds(form?.studentIds)
  }

  if (notes) {
    payload.notes = notes
  }

  return payload
}

/**
 * @param {Record<string, unknown>} form
 * @returns {{
 *   points: number
 *   student_ids: number[]
 *   reason: string
 * } | FormData}
 */
export function buildRedeemPointsPayload(form) {
  const points = Number(form?.pointsUsed)
  const reason = String(form?.rewardReason ?? "").trim()

  if (isBehaviourStudentCsvSelection(form)) {
    const formData = new FormData()
    formData.append("points", String(points))
    formData.append("reason", reason)
    formData.append("csv_file", form.csvFile)
    return formData
  }

  return {
    points,
    student_ids: normalizeBehaviourStudentIds(form?.studentIds),
    reason
  }
}

/**
 * @param {Record<string, unknown>} form
 * @returns {{
 *   points: number
 *   student_ids: number[]
 *   reason: string
 * } | FormData}
 */
export function buildCorrectionPointsPayload(form) {
  const points = Number(form?.points)
  const reason = String(form?.reason ?? "").trim()

  if (isBehaviourStudentCsvSelection(form)) {
    const formData = new FormData()
    formData.append("points", String(points))
    formData.append("reason", reason)
    formData.append("csv_file", form.csvFile)
    return formData
  }

  return {
    points,
    student_ids: normalizeBehaviourStudentIds(form?.studentIds),
    reason
  }
}

/**
 * @param {unknown} err
 * @param {BehaviourPointsOperation} operation
 * @returns {{ fieldErrors: Record<string, string>, message: string }}
 */
export function parseBehaviourPointsApiError(err, operation) {
  const data = err?.response?.data
  const fieldErrors = {}

  const errorsObj = data?.errors
  if (errorsObj && typeof errorsObj === "object") {
    Object.entries(errorsObj).forEach(([apiKey, messages]) => {
      const formField = mapBehaviourPointsApiFieldToFormField(apiKey, operation)
      if (!formField) return
      const message = extractFirstMessage(messages)
      if (message && !fieldErrors[formField]) {
        fieldErrors[formField] = message
      }
    })
  }

  const message =
    String(data?.error ?? data?.message ?? err?.message ?? "").trim() || ""

  return { fieldErrors, message }
}

/**
 * @param {Record<string, unknown> | null | undefined} body
 * @returns {{
 *   transactionIds: number[],
 *   count: number,
 *   importLogId: number | null,
 *   message: string,
 *   status: string,
 *   isBackgroundJob: boolean
 * }}
 */
export function parseBehaviourPointsSubmitResponse(body) {
  const transactionIds = (body?.transaction_ids ?? [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id))

  const importLogIdRaw = Number(body?.import_log_id)
  const importLogId = Number.isFinite(importLogIdRaw) ? importLogIdRaw : null
  const status = String(body?.status ?? "").trim()
  const message = String(body?.message ?? "").trim()
  const count = Number(body?.count) || transactionIds.length

  // Background dispatch: status + import_log_id, no transaction_ids yet.
  // Sync completion: transaction_ids (+ optional import_log_id), no status.
  const isBackgroundJob =
    Boolean(status) && transactionIds.length === 0 && importLogId != null

  return {
    transactionIds,
    count,
    importLogId,
    message,
    status,
    isBackgroundJob
  }
}
